/**
 * Quote Management Hub - Unified Component
 * 
 * Combines quote response entry, comparison, selection, and order conversion
 * Features:
 * - Manual quote entry with email parsing capabilities
 * - Side-by-side quote comparison matrix
 * - Supplier selection with confirmation lock
 * - Dual order conversion: Draft vs Send immediately
 * - Workflow state integration
 * - Automated supplier notifications
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Tooltip,
  Divider,
  Switch,
  FormGroup,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Drafts as DraftIcon,
  Compare as CompareIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Euro as EuroIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import EmailQuoteSelectionDialog from './EmailQuoteSelectionDialog';
import { SupplierQuoteEmailParser, type ParsedSupplierQuote } from '../../../services/supplierQuoteEmailParser';
import { type QuoteEmailCandidate } from '../../../services/quoteEmailFetcher';

interface QuoteManagementHubProps {
  caseId: string;
  caseNumber: string;
  currentWorkflowState?: string;
  onQuoteSelected?: (quote: any) => void;
  onOrderConverted?: (orderId: string) => void;
  onWorkflowTransition?: (newState: string) => void;
}

interface CaseSupplier {
  id: string;
  name: string;
  email: string;
  company: string;
}

interface CasePosition {
  position_id: string;
  position_number: string;
  part_name: string;
  description?: string;
  quantity: number;
  part_number?: string;
}

interface QuotedPosition {
  position_number: string;
  part_number: string;
  part_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  delivery_days: number;
  supplier_notes?: string;
}

interface SupplierQuote {
  quote_id: string;
  supplier_id: string;
  supplier_name: string;
  supplier_email: string;
  total_quote_value: number;
  currency: string;
  delivery_time_days: number;
  quote_valid_until: string;
  payment_terms: string;
  delivery_terms: string;
  warranty_terms: string;
  special_conditions: string;
  notes: string;
  quoted_positions: QuotedPosition[];
  response_date: string;
  is_selected?: boolean;
  selection_rationale?: string;
}

interface EmailParsingResult {
  supplier_email: string;
  total_price?: number;
  delivery_days?: number;
  currency?: string;
  positions?: Partial<QuotedPosition>[];
  confidence: number;
  raw_content: string;
}

const QuoteManagementHub: React.FC<QuoteManagementHubProps> = ({
  caseId,
  caseNumber,
  currentWorkflowState,
  onQuoteSelected,
  onOrderConverted,
  onWorkflowTransition
}) => {
  const { showNotification } = useNotification();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [caseSuppliers, setCaseSuppliers] = useState<CaseSupplier[]>([]);
  const [casePositions, setCasePositions] = useState<CasePosition[]>([]);
  const [quotes, setQuotes] = useState<SupplierQuote[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<SupplierQuote | null>(null);
  const [supplierLocked, setSupplierLocked] = useState(false);
  
  // Form states
  const [newQuote, setNewQuote] = useState<Partial<SupplierQuote>>({
    supplier_id: '',
    supplier_name: '',
    supplier_email: '',
    total_quote_value: 0,
    currency: 'EUR',
    delivery_time_days: 14,
    quote_valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    payment_terms: '30 Tage netto',
    delivery_terms: 'EXW',
    warranty_terms: '24 Monate',
    special_conditions: '',
    notes: '',
    quoted_positions: []
  });
  
  // Dialog states
  const [emailParsingDialogOpen, setEmailParsingDialogOpen] = useState(false);
  const [emailContent, setEmailContent] = useState('');
  const [emailSelectionDialogOpen, setEmailSelectionDialogOpen] = useState(false);
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
  const [conversionMode, setConversionMode] = useState<'draft' | 'send'>('draft');
  const [pdfLanguage, setPdfLanguage] = useState<'de' | 'en'>('de');
  const [supplierLockConfirmOpen, setSupplierLockConfirmOpen] = useState(false);

  // Load case data on mount
  useEffect(() => {
    if (caseId) {
      loadCaseData();
      loadExistingQuotes();
    }
  }, [caseId]);

  const loadCaseData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v3/cases/${caseId}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        const suppliers = result.data.metadata?.suppliers || [];
        setCaseSuppliers(suppliers);
        
        const positions = result.data.metadata?.positions || result.data.positions || [];
        setCasePositions(positions);
        
        // Auto-populate quoted positions from case positions
        if (positions.length > 0 && newQuote.quoted_positions?.length === 0) {
          const autoPositions = positions.map((pos: CasePosition) => ({
            position_number: pos.position_number,
            part_number: pos.part_number || '',
            part_name: pos.part_name,
            quantity: pos.quantity,
            unit_price: 0,
            total_price: 0,
            delivery_days: newQuote.delivery_time_days || 14,
            supplier_notes: ''
          }));
          
          setNewQuote(prev => ({
            ...prev,
            quoted_positions: autoPositions
          }));
        }
      }
    } catch (error) {
      console.error('Error loading case data:', error);
      showNotification('Fehler beim Laden der Fall-Daten', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingQuotes = async () => {
    try {
      const response = await fetch(`/api/v3/quotes/${caseId}/responses`);
      const result = await response.json();
      
      if (result.success && result.data?.quote_responses) {
        const quoteResponses = result.data.quote_responses;
        const fixatedSupplier = result.data.fixated_supplier;
        const selectedQuote = result.data.selected_quote;

        console.log('🔍 [QUOTE-LOADING] Loading quotes with selection state:', {
          total_quotes: quoteResponses.length,
          fixated_supplier: fixatedSupplier ? fixatedSupplier.supplier_name : 'none',
          selected_quote: selectedQuote ? selectedQuote.quote_id : 'none',
          quotes_with_selection: quoteResponses.map((q: any) => ({
            quote_id: q.quote_id,
            supplier_name: q.supplier_name,
            is_selected: q.is_selected || q.selected || false
          }))
        });

        setQuotes(quoteResponses);

        // If there's a fixated supplier and selected quote, restore the UI state
        if (fixatedSupplier && selectedQuote) {
          console.log('🔧 [QUOTE-LOADING] Restoring selected quote state from fixated supplier:', {
            supplier_name: selectedQuote.supplier_name,
            quote_id: selectedQuote.quote_id,
            selected_at: selectedQuote.selected_at
          });

          setSelectedQuote(selectedQuote);
          setSupplierLocked(true);
          showNotification(
            `Lieferant ${fixatedSupplier.supplier_name} bereits fixiert (${new Date(fixatedSupplier.fixated_at).toLocaleDateString()})`,
            'info'
          );
        } else if (selectedQuote) {
          // Selected quote exists but no fixation - restore selection state
          console.log('🎯 [QUOTE-LOADING] Restoring selected quote state without fixation:', {
            quote_id: selectedQuote.quote_id,
            supplier_name: selectedQuote.supplier_name,
            selected_at: selectedQuote.selected_at
          });

          setSelectedQuote(selectedQuote);
          showNotification(
            `Angebot von ${selectedQuote.supplier_name} bereits ausgewählt`,
            'info'
          );
        } else {
          // Check if any quote in responses is marked as selected (fallback)
          const selectedQuoteResponse = quoteResponses.find((q: any) => q.is_selected || q.selected);
          if (selectedQuoteResponse) {
            console.log('🎯 [QUOTE-LOADING] Found selected quote in responses (fallback):', {
              quote_id: selectedQuoteResponse.quote_id,
              supplier_name: selectedQuoteResponse.supplier_name
            });

            setSelectedQuote(selectedQuoteResponse);
            showNotification(
              `Angebot von ${selectedQuoteResponse.supplier_name} bereits ausgewählt`,
              'info'
            );
          }
        }
      }
    } catch (error) {
      console.error('Error loading existing quotes:', error);
    }
  };

  // Email parsing functionality
  const parseEmailContent = (content: string): EmailParsingResult => {
    const result: EmailParsingResult = {
      supplier_email: '',
      confidence: 0,
      raw_content: content
    };

    // Extract email address
    const emailMatch = content.match(/[\w.-]+@[\w.-]+\.\w+/);
    if (emailMatch) {
      result.supplier_email = emailMatch[0];
      result.confidence += 0.2;
    }

    // Extract prices (€, EUR, Euro patterns)
    const pricePatterns = [
      /(?:€|EUR|Euro)\s*([0-9,.]+)/gi,
      /([0-9,.]+)\s*(?:€|EUR|Euro)/gi,
      /(?:Preis|Price|Total|Gesamt)[\s:]*([0-9,.]+)/gi
    ];

    for (const pattern of pricePatterns) {
      const matches = content.match(pattern);
      if (matches) {
        const numericValue = matches[0].replace(/[^0-9,.]/g, '').replace(',', '.');
        const price = parseFloat(numericValue);
        if (!isNaN(price) && price > 0) {
          result.total_price = price;
          result.confidence += 0.3;
          break;
        }
      }
    }

    // Extract delivery times
    const deliveryPatterns = [
      /(?:Lieferzeit|Delivery|Lieferung)[\s:]*([0-9]+)[\s]*(?:Tage|days|Wochen|weeks)/gi,
      /([0-9]+)[\s]*(?:Tage|days)[\s]*(?:Lieferzeit|delivery)/gi
    ];

    for (const pattern of deliveryPatterns) {
      const matches = content.match(pattern);
      if (matches) {
        const deliveryMatch = matches[0].match(/([0-9]+)/);
        if (deliveryMatch) {
          let days = parseInt(deliveryMatch[1]);
          if (content.toLowerCase().includes('wochen') || content.toLowerCase().includes('weeks')) {
            days *= 7; // Convert weeks to days
          }
          result.delivery_days = days;
          result.confidence += 0.2;
          break;
        }
      }
    }

    // Extract currency
    if (content.includes('€') || content.toLowerCase().includes('eur')) {
      result.currency = 'EUR';
      result.confidence += 0.1;
    } else if (content.includes('$') || content.toLowerCase().includes('usd')) {
      result.currency = 'USD';
      result.confidence += 0.1;
    }

    // Simple position extraction (basic implementation)
    const positionPatterns = [
      /(?:Position|Pos\.?|Item)[\s]*([0-9]+)[\s]*[:-][\s]*([^\n\r]+)/gi,
      /([0-9]+)[\s]*[.-][\s]*([^\n\r]+?)[\s]*€?[\s]*([0-9,.]+)/gi
    ];

    const extractedPositions: Partial<QuotedPosition>[] = [];
    for (const pattern of positionPatterns) {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        if (match.length >= 3) {
          extractedPositions.push({
            position_number: match[1],
            part_name: match[2].trim(),
            unit_price: match[3] ? parseFloat(match[3].replace(',', '.')) : 0
          });
        }
      }
      // Reset regex lastIndex for next pattern
      pattern.lastIndex = 0;
    }

    if (extractedPositions.length > 0) {
      result.positions = extractedPositions;
      result.confidence += 0.2;
    }

    return result;
  };

  const handleEmailParsing = () => {
    if (!emailContent.trim()) {
      showNotification('Bitte geben Sie E-Mail-Inhalt ein', 'error');
      return;
    }

    const parsed = parseEmailContent(emailContent);
    
    if (parsed.confidence < 0.3) {
      showNotification(`Automatische Erkennung unvollständig (${Math.round(parsed.confidence * 100)}% Konfidenz). Bitte prüfen Sie die Werte.`, 'warning');
    } else {
      showNotification(`E-Mail erfolgreich geparst (${Math.round(parsed.confidence * 100)}% Konfidenz)`, 'success');
    }

    // Apply parsed data to form
    if (parsed.supplier_email) {
      const matchingSupplier = caseSuppliers.find(s => 
        s.email.toLowerCase() === parsed.supplier_email.toLowerCase()
      );
      if (matchingSupplier) {
        setNewQuote(prev => ({
          ...prev,
          supplier_id: matchingSupplier.id,
          supplier_name: matchingSupplier.name,
          supplier_email: matchingSupplier.email
        }));
      }
    }

    if (parsed.total_price) {
      setNewQuote(prev => ({
        ...prev,
        total_quote_value: parsed.total_price!
      }));
    }

    if (parsed.delivery_days) {
      setNewQuote(prev => ({
        ...prev,
        delivery_time_days: parsed.delivery_days!
      }));
    }

    if (parsed.currency) {
      setNewQuote(prev => ({
        ...prev,
        currency: parsed.currency!
      }));
    }

    setEmailParsingDialogOpen(false);
    setEmailContent('');
  };

  // Handle email selection from EmailQuoteSelectionDialog
  const handleEmailSelected = (parsedData: ParsedSupplierQuote, rawEmail: QuoteEmailCandidate) => {
    console.log('📧 Processing selected email from:', rawEmail.from_address);
    
    try {
      // Convert parsed data to quote form format
      const convertedData = SupplierQuoteEmailParser.convertToQuoteForm(parsedData);
      
      // Find matching supplier
      const matchingSupplier = caseSuppliers.find(s => 
        s.email.toLowerCase() === parsedData.supplierInfo.supplier_email.toLowerCase()
      );
      
      // Apply parsed data to form
      setNewQuote(prev => ({
        ...prev,
        ...convertedData,
        supplier_id: matchingSupplier?.id || '',
        supplier_name: matchingSupplier?.name || parsedData.supplierInfo.supplier_name || '',
        supplier_email: parsedData.supplierInfo.supplier_email
      }));
      
      // Show success notification with confidence info
      const confidence = parsedData.extractionMetadata.confidence;
      const confidenceLabels = { high: 'Hoch', medium: 'Mittel', low: 'Niedrig' };
      
      showNotification(
        `E-Mail erfolgreich geparst! Vertrauen: ${confidenceLabels[confidence]} (${parsedData.positions.length} Positionen erkannt)`,
        confidence === 'high' ? 'success' : confidence === 'medium' ? 'info' : 'warning'
      );
      
      console.log('✅ Email parsing complete:', {
        confidence,
        positions: parsedData.positions.length,
        totalValue: parsedData.quoteInfo.total_quote_value,
        supplier: parsedData.supplierInfo.supplier_name
      });
      
    } catch (error) {
      console.error('❌ Error processing selected email:', error);
      showNotification('Fehler beim Verarbeiten der ausgewählten E-Mail', 'error');
    }
  };

  const addQuotePosition = () => {
    const newPosition: QuotedPosition = {
      position_number: (newQuote.quoted_positions?.length || 0 + 1).toString(),
      part_number: '',
      part_name: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      delivery_days: newQuote.delivery_time_days || 14,
      supplier_notes: ''
    };
    
    setNewQuote(prev => ({
      ...prev,
      quoted_positions: [...(prev.quoted_positions || []), newPosition]
    }));
  };

  const updateQuotePosition = (index: number, field: keyof QuotedPosition, value: any) => {
    const updatedPositions = [...(newQuote.quoted_positions || [])];
    updatedPositions[index] = { ...updatedPositions[index], [field]: value };
    
    // Auto-calculate total price
    if (field === 'unit_price' || field === 'quantity') {
      updatedPositions[index].total_price = updatedPositions[index].unit_price * updatedPositions[index].quantity;
    }
    
    const totalValue = updatedPositions.reduce((sum, pos) => sum + pos.total_price, 0);
    
    setNewQuote(prev => ({
      ...prev,
      quoted_positions: updatedPositions,
      total_quote_value: totalValue
    }));
  };

  const removeQuotePosition = (index: number) => {
    const updatedPositions = (newQuote.quoted_positions || []).filter((_, i) => i !== index);
    const totalValue = updatedPositions.reduce((sum, pos) => sum + pos.total_price, 0);
    
    setNewQuote(prev => ({
      ...prev,
      quoted_positions: updatedPositions,
      total_quote_value: totalValue
    }));
  };

  const saveQuote = async () => {
    // Validation
    if (!newQuote.supplier_id || !newQuote.supplier_name) {
      showNotification('Bitte wählen Sie einen Lieferanten aus', 'error');
      return;
    }

    if (!newQuote.quoted_positions || newQuote.quoted_positions.length === 0) {
      showNotification('Bitte fügen Sie mindestens eine Position hinzu', 'error');
      return;
    }

    setLoading(true);
    try {
      const quoteData = {
        quote_id: `manual-quote-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        ...newQuote,
        received_at: new Date().toISOString(),
        response_date: new Date().toISOString()
      };

      const response = await fetch(`/api/v3/cases/${caseId}/add-quote-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quote_response: quoteData })
      });

      const result = await response.json();

      if (result.success) {
        showNotification('Angebot erfolgreich gespeichert', 'success');
        await loadExistingQuotes();
        
        // Reset form
        setNewQuote({
          supplier_id: '',
          supplier_name: '',
          supplier_email: '',
          total_quote_value: 0,
          currency: 'EUR',
          delivery_time_days: 14,
          quote_valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          payment_terms: '30 Tage netto',
          delivery_terms: 'EXW',
          warranty_terms: '24 Monate',
          special_conditions: '',
          notes: '',
          quoted_positions: []
        });
        
        // Switch to comparison tab
        setActiveTab(1);
        
        // Trigger workflow transition if needed
        if (onWorkflowTransition) {
          onWorkflowTransition('supplier_response_received');
        }
      } else {
        showNotification(result.message || 'Fehler beim Speichern', 'error');
      }
    } catch (error: any) {
      showNotification(`Fehler beim Speichern: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectQuote = async (quote: SupplierQuote, rationale: string) => {
    const updatedQuote = { ...quote, is_selected: true, selection_rationale: rationale };
    setSelectedQuote(updatedQuote);
    setSupplierLocked(true);
    
    console.log('🎯 [QUOTE-SELECTION] Selecting quote and calling selection API', {
      selected_quote_id: quote.quote_id,
      supplier_id: quote.supplier_id,
      supplier_name: quote.supplier_name,
      case_id: caseId,
      selection_rationale: rationale
    });

    // Step 1: Mark quote as selected in backend
    try {
      const selectionResponse = await fetch(`/api/v3/cases/${caseId}/select-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quote_id: quote.quote_id,
          selection_rationale: rationale
        })
      });

      const selectionResult = await selectionResponse.json();
      
      if (selectionResult.success) {
        console.log('✅ [QUOTE-SELECTION] Quote selection successful:', selectionResult);
        showNotification(`Angebot von ${quote.supplier_name} ausgewählt`, 'success');
      } else {
        console.error('❌ [QUOTE-SELECTION] Quote selection failed:', selectionResult);
        showNotification(`Angebot-Auswahl fehlgeschlagen: ${selectionResult.message}`, 'warning');
        return; // Don't proceed to fixation if selection failed
      }
    } catch (error) {
      console.error('❌ [QUOTE-SELECTION] Error calling selection API:', error);
      showNotification('Fehler bei der Angebot-Auswahl', 'warning');
      return; // Don't proceed to fixation if selection failed
    }

    // Step 2: Call supplier fixation API to remove non-selected suppliers
    try {
      const fixationResponse = await fetch(`/api/v3/cases/${caseId}/fixate-supplier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selected_supplier_id: quote.supplier_id,
          selected_quote_id: quote.quote_id
        })
      });

      const fixationResult = await fixationResponse.json();
      
      if (fixationResult.success) {
        console.log('✅ [SUPPLIER-FIXATION] Supplier fixation successful:', fixationResult);
        showNotification(
          `${quote.supplier_name} fixiert. ${fixationResult.data.removed_suppliers_count} andere Lieferanten entfernt.`, 
          'success'
        );
      } else {
        console.error('❌ [SUPPLIER-FIXATION] Supplier fixation failed:', fixationResult);
        showNotification(`Lieferant-Fixierung fehlgeschlagen: ${fixationResult.message}`, 'warning');
      }
    } catch (error) {
      console.error('❌ [SUPPLIER-FIXATION] Error calling fixation API:', error);
      showNotification('Fehler bei der Lieferant-Fixierung', 'warning');
    }
    
    if (onQuoteSelected) {
      onQuoteSelected(updatedQuote);
    }
  };

  const handleConvertToOrder = async () => {
    if (!selectedQuote) {
      showNotification('Bitte wählen Sie zuerst ein Angebot aus', 'error');
      return;
    }

    setLoading(true);
    try {
      const conversionData = {
        selected_quote_id: selectedQuote.quote_id,
        auto_generate_pdf: conversionMode === 'send',
        auto_send_email: conversionMode === 'send',
        language: pdfLanguage
      };

      const response = await fetch(`/api/v3/quotes/${caseId}/convert-to-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(conversionData)
      });

      const result = await response.json();

      if (result.success) {
        const orderId = result.data.order_id;
        
        if (conversionMode === 'send') {
          showNotification('Bestellung erstellt und E-Mail versendet', 'success');
          
          // Send notifications to losing suppliers
          await notifyLosingSuppliers(selectedQuote.supplier_id);
        } else {
          showNotification('Bestellung als Entwurf erstellt', 'success');
        }
        
        if (onOrderConverted) {
          onOrderConverted(orderId);
        }
        
        // Trigger workflow transition
        if (onWorkflowTransition) {
          onWorkflowTransition('converted_to_order');
        }
        
        setConversionDialogOpen(false);
      } else {
        showNotification(result.message || 'Fehler bei der Konvertierung', 'error');
      }
    } catch (error: any) {
      showNotification(`Fehler bei der Konvertierung: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const notifyLosingSuppliers = async (winningSupplierEmail: string) => {
    try {
      const losingSuppliers = quotes.filter(q => q.supplier_email !== winningSupplierEmail);
      
      for (const supplier of losingSuppliers) {
        // Send professional decline email
        await fetch('/api/v3/email/send-supplier-notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: supplier.supplier_email,
            type: 'quote_declined',
            case_number: caseNumber,
            supplier_name: supplier.supplier_name,
            language: 'de'
          })
        });
      }
      
      showNotification(`${losingSuppliers.length} Lieferanten über Entscheidung informiert`, 'info');
    } catch (error) {
      console.error('Error notifying losing suppliers:', error);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getBestQuote = () => {
    if (quotes.length === 0) return null;
    return quotes.reduce((best, current) => 
      current.total_quote_value < best.total_quote_value ? current : best
    );
  };

  const getFastestQuote = () => {
    if (quotes.length === 0) return null;
    return quotes.reduce((fastest, current) => 
      current.delivery_time_days < fastest.delivery_time_days ? current : fastest
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <CompareIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Angebote Verwalten - {caseNumber}
        </Typography>
        
        <Box display="flex" gap={2} alignItems="center">
          {selectedQuote && (
            <Chip
              icon={supplierLocked ? <LockIcon /> : <LockOpenIcon />}
              label={`Ausgewählt: ${selectedQuote.supplier_name}`}
              color="primary"
              variant={supplierLocked ? "filled" : "outlined"}
            />
          )}
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadExistingQuotes}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {/* Status Alert */}
      {quotes.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography>
              <strong>{quotes.length} Angebot(e) erhalten</strong> - 
              Bestes Angebot: {formatCurrency(getBestQuote()?.total_quote_value || 0)} ({getBestQuote()?.supplier_name})
              {getFastestQuote() && ` | Schnellste Lieferung: ${getFastestQuote()?.delivery_time_days} Tage (${getFastestQuote()?.supplier_name})`}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Main Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab 
            label={
              <Badge badgeContent={newQuote.quoted_positions?.length || 0} color="primary">
                Angebot Eingeben
              </Badge>
            } 
            icon={<AddIcon />} 
          />
          <Tab 
            label={
              <Badge badgeContent={quotes.length} color="secondary">
                Angebote Vergleichen
              </Badge>
            } 
            icon={<CompareIcon />} 
          />
          <Tab 
            label="Lieferant Auswählen" 
            icon={<BusinessIcon />}
            disabled={quotes.length === 0}
          />
        </Tabs>
      </Paper>

      {/* Tab 1: Quote Entry */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Email Parsing Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Automatische E-Mail Auswertung
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<EmailIcon />}
                      onClick={() => setEmailSelectionDialogOpen(true)}
                      color="primary"
                    >
                      Aus E-Mails Abrufen
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setEmailParsingDialogOpen(true)}
                    >
                      Manuell Parsen
                    </Button>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Aus E-Mails Abrufen:</strong> Automatisches Laden und Parsen von eingehenden Angebots-E-Mails für diesen Fall.<br/>
                  <strong>Manuell Parsen:</strong> E-Mail-Inhalte einfügen zur automatischen Erkennung von Preisen, Lieferzeiten und Positionen.
                </Typography>
              </CardContent>
            </Card>

            {/* Quote Form */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Neues Angebot erfassen
              </Typography>
              
              <Grid container spacing={3}>
                {/* Supplier Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Lieferant auswählen *</InputLabel>
                    <Select
                      value={newQuote.supplier_id || ''}
                      onChange={(e) => {
                        const supplier = caseSuppliers.find(s => s.id === e.target.value);
                        if (supplier) {
                          setNewQuote(prev => ({
                            ...prev,
                            supplier_id: supplier.id,
                            supplier_name: supplier.name,
                            supplier_email: supplier.email
                          }));
                        }
                      }}
                    >
                      {caseSuppliers.map(supplier => (
                        <MenuItem key={supplier.id} value={supplier.id}>
                          {supplier.name} ({supplier.company}) - {supplier.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Basic Quote Data */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Gesamtwert"
                    type="number"
                    value={newQuote.total_quote_value || 0}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, total_quote_value: parseFloat(e.target.value) || 0 }))}
                    InputProps={{ startAdornment: '€' }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Lieferzeit (Tage)"
                    type="number"
                    value={newQuote.delivery_time_days || 14}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, delivery_time_days: parseInt(e.target.value) || 14 }))}
                  />
                </Grid>

                {/* Terms */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Zahlungsbedingungen"
                    value={newQuote.payment_terms || ''}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, payment_terms: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Lieferbedingungen"
                    value={newQuote.delivery_terms || ''}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, delivery_terms: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Gültig bis"
                    type="date"
                    value={newQuote.quote_valid_until || ''}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, quote_valid_until: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Anmerkungen"
                    value={newQuote.notes || ''}
                    onChange={(e) => setNewQuote(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Positions Table */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Positionen</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addQuotePosition}
                >
                  Position hinzufügen
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Pos.</TableCell>
                      <TableCell>Artikel-Nr.</TableCell>
                      <TableCell>Bezeichnung</TableCell>
                      <TableCell>Menge</TableCell>
                      <TableCell>Einzelpreis</TableCell>
                      <TableCell>Gesamtpreis</TableCell>
                      <TableCell>Lieferzeit</TableCell>
                      <TableCell>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(newQuote.quoted_positions || []).map((position, index) => (
                      <TableRow key={index}>
                        <TableCell>{position.position_number}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={position.part_number}
                            onChange={(e) => updateQuotePosition(index, 'part_number', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={position.part_name}
                            onChange={(e) => updateQuotePosition(index, 'part_name', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={position.quantity}
                            onChange={(e) => updateQuotePosition(index, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={position.unit_price}
                            onChange={(e) => updateQuotePosition(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            InputProps={{ startAdornment: '€' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(position.total_price)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={position.delivery_days}
                            onChange={(e) => updateQuotePosition(index, 'delivery_days', parseInt(e.target.value) || 14)}
                            InputProps={{ endAdornment: 'Tage' }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeQuotePosition(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Save Button */}
              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveQuote}
                  disabled={loading || !newQuote.supplier_id}
                  size="large"
                >
                  Angebot Speichern
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Quote Comparison */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {quotes.length === 0 ? (
              <Alert severity="info">
                Noch keine Angebote vorhanden. Wechseln Sie zur ersten Registerkarte, um Angebote hinzuzufügen.
              </Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Lieferant</TableCell>
                      <TableCell align="right">Gesamtpreis</TableCell>
                      <TableCell align="center">Lieferzeit</TableCell>
                      <TableCell>Zahlungsbedingungen</TableCell>
                      <TableCell>Gültig bis</TableCell>
                      <TableCell align="center">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quotes.map((quote, index) => (
                      <TableRow 
                        key={quote.quote_id}
                        sx={{ 
                          backgroundColor: quote.is_selected ? 'action.selected' : 'inherit',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {quote.supplier_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {quote.supplier_email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="h6" 
                            color={quote === getBestQuote() ? 'success.main' : 'inherit'}
                            fontWeight={quote === getBestQuote() ? 'bold' : 'normal'}
                          >
                            {formatCurrency(quote.total_quote_value, quote.currency)}
                          </Typography>
                          {quote === getBestQuote() && (
                            <Chip label="Günstigster" color="success" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            color={quote === getFastestQuote() ? 'info.main' : 'inherit'}
                            fontWeight={quote === getFastestQuote() ? 'bold' : 'normal'}
                          >
                            {quote.delivery_time_days} Tage
                          </Typography>
                          {quote === getFastestQuote() && (
                            <Chip label="Schnellster" color="info" size="small" />
                          )}
                        </TableCell>
                        <TableCell>{quote.payment_terms}</TableCell>
                        <TableCell>{formatDate(quote.quote_valid_until)}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant={quote.is_selected ? "contained" : "outlined"}
                            size="small"
                            startIcon={quote.is_selected ? <CheckCircleIcon /> : <BusinessIcon />}
                            onClick={() => {
                              if (!quote.is_selected) {
                                setActiveTab(2); // Switch to selection tab
                                setSelectedQuote(quote);
                              }
                            }}
                            disabled={supplierLocked && !quote.is_selected}
                          >
                            {quote.is_selected ? 'Ausgewählt' : 'Auswählen'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      )}

      {/* Tab 3: Supplier Selection & Order Conversion */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {!selectedQuote ? (
              <Alert severity="warning">
                Bitte wählen Sie zuerst ein Angebot aus der Vergleichstabelle aus.
              </Alert>
            ) : (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Ausgewähltes Angebot bestätigen
                </Typography>
                
                <Box mb={3}>
                  <Alert severity="success">
                    <Typography variant="subtitle1" fontWeight="bold">
                      {selectedQuote.supplier_name}
                    </Typography>
                    <Typography>
                      Gesamtpreis: {formatCurrency(selectedQuote.total_quote_value, selectedQuote.currency)} | 
                      Lieferzeit: {selectedQuote.delivery_time_days} Tage
                    </Typography>
                  </Alert>
                </Box>

                {/* Selection Rationale */}
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Auswahlbegründung (erforderlich)"
                  value={selectedQuote.selection_rationale || ''}
                  onChange={(e) => setSelectedQuote(prev => prev ? { ...prev, selection_rationale: e.target.value } : null)}
                  sx={{ mb: 3 }}
                />

                {/* Supplier Lock */}
                <Box mb={3}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={supplierLocked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSupplierLockConfirmOpen(true);
                            } else {
                              setSupplierLocked(false);
                            }
                          }}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center">
                          {supplierLocked ? <LockIcon sx={{ mr: 1 }} /> : <LockOpenIcon sx={{ mr: 1 }} />}
                          Lieferant fixieren (verhindert weitere Änderungen)
                        </Box>
                      }
                    />
                  </FormGroup>
                </Box>

                {/* Conversion Buttons */}
                <Box display="flex" gap={2}>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<DraftIcon />}
                    onClick={() => {
                      setConversionMode('draft');
                      setConversionDialogOpen(true);
                    }}
                    disabled={!supplierLocked || !selectedQuote.selection_rationale?.trim()}
                  >
                    Als Entwurf erstellen
                  </Button>
                  
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SendIcon />}
                    onClick={() => {
                      setConversionMode('send');
                      setConversionDialogOpen(true);
                    }}
                    disabled={!supplierLocked || !selectedQuote.selection_rationale?.trim()}
                  >
                    Erstellen & Senden
                  </Button>
                </Box>
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, position: 'sticky', top: 16 }}>
              <Typography variant="h6" gutterBottom>
                Konvertierung Übersicht
              </Typography>
              
              {selectedQuote && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Ausgewählter Lieferant:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" gutterBottom>
                    {selectedQuote.supplier_name}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Gesamtwert:
                  </Typography>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {formatCurrency(selectedQuote.total_quote_value, selectedQuote.currency)}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Positionen:
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedQuote.quoted_positions.length} Position(en)
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="body2" color="text.secondary">
                    Nach der Konvertierung:
                  </Typography>
                  <Typography variant="body2">
                    • Bestellung wird im System erstellt
                    • Bei "Senden": PDF wird generiert und E-Mail versendet
                    • Verlierende Lieferanten werden automatisch benachrichtigt
                    • Workflow-Status wird aktualisiert
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Email Parsing Dialog */}
      <Dialog open={emailParsingDialogOpen} onClose={() => setEmailParsingDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          E-Mail Inhalt automatisch auswerten
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Fügen Sie den E-Mail-Inhalt ein. Das System erkennt automatisch Preise, Lieferzeiten und Positionen.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            label="E-Mail Inhalt"
            value={emailContent}
            onChange={(e) => setEmailContent(e.target.value)}
            placeholder="Hier den E-Mail-Inhalt einfügen..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailParsingDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button 
            variant="contained" 
            onClick={handleEmailParsing}
            disabled={!emailContent.trim()}
          >
            Auswerten
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conversion Dialog */}
      <Dialog open={conversionDialogOpen} onClose={() => setConversionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {conversionMode === 'send' ? (
            <><SendIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Bestellung erstellen und senden</>
          ) : (
            <><DraftIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Bestellung als Entwurf erstellen</>
          )}
        </DialogTitle>
        <DialogContent>
          {conversionMode === 'send' && (
            <Box mb={3}>
              <Typography variant="body2" gutterBottom>
                PDF Sprache:
              </Typography>
              <RadioGroup
                value={pdfLanguage}
                onChange={(e) => setPdfLanguage(e.target.value as 'de' | 'en')}
                row
              >
                <FormControlLabel value="de" control={<Radio />} label="Deutsch" />
                <FormControlLabel value="en" control={<Radio />} label="English" />
              </RadioGroup>
            </Box>
          )}
          
          <Typography variant="body1">
            {conversionMode === 'send' 
              ? `Die Bestellung wird erstellt, als PDF generiert und automatisch an ${selectedQuote?.supplier_email} gesendet. Alle anderen Lieferanten erhalten eine höfliche Absage.`
              : 'Die Bestellung wird als Entwurf erstellt und kann später über die Bestellhistorie finalisiert und versendet werden.'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConversionDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button 
            variant="contained" 
            onClick={handleConvertToOrder}
            disabled={loading}
            color={conversionMode === 'send' ? 'primary' : 'secondary'}
          >
            {conversionMode === 'send' ? 'Erstellen & Senden' : 'Als Entwurf erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Lock Confirmation */}
      <Dialog open={supplierLockConfirmOpen} onClose={() => setSupplierLockConfirmOpen(false)}>
        <DialogTitle>
          <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Lieferant fixieren
        </DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie den ausgewählten Lieferanten wirklich fixieren? 
            Nach der Fixierung können keine Änderungen mehr vorgenommen werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupplierLockConfirmOpen(false)}>
            Abbrechen
          </Button>
          <Button 
            variant="contained" 
            onClick={async () => {
              setSupplierLockConfirmOpen(false);
              
              console.log('🔧 [QUOTE-MANAGEMENT] Supplier lock confirmation - selectedQuote:', selectedQuote);
              console.log('🔧 [QUOTE-MANAGEMENT] selection_rationale:', selectedQuote?.selection_rationale);
              
              // Step 1: First select the quote (sets metadata)
              if (selectedQuote) {
                const rationale = selectedQuote.selection_rationale?.trim() || 'No rationale provided';
                try {
                  console.log('🎯 [QUOTE-MANAGEMENT] Calling selectQuote before supplier lock with rationale:', rationale);
                  await selectQuote(selectedQuote, rationale);
                  console.log('✅ [QUOTE-MANAGEMENT] selectQuote completed successfully');
                } catch (error) {
                  console.error('❌ [QUOTE-MANAGEMENT] Failed to select quote:', error);
                  showNotification('Fehler bei der Angebot-Auswahl', 'error');
                  return;
                }
              } else {
                console.error('❌ [QUOTE-MANAGEMENT] No selectedQuote available');
                showNotification('Kein Angebot ausgewählt', 'error');
                return;
              }
              
              // Step 2: Lock supplier and transition workflow state
              setSupplierLocked(true);
              
              // Transition workflow state to quote_selected when supplier is locked
              try {
                console.log('🔒 [QUOTE-MANAGEMENT] Supplier locked - transitioning to quote_selected state');
                const transitionResponse = await fetch(`/api/v3/workflow/cases/${caseId}/transition`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    to_state: 'quote_selected',
                    metadata: {
                      supplier_locked: true,
                      supplier_lock_timestamp: new Date().toISOString(),
                      selected_quote_id: selectedQuote?.quote_id,
                      selection_rationale: selectedQuote?.selection_rationale
                    }
                  })
                });
                
                const transitionResult = await transitionResponse.json();
                if (transitionResult.success) {
                  console.log('✅ [QUOTE-MANAGEMENT] Workflow state transitioned to quote_selected');
                  showNotification('Lieferant fixiert und Angebot ausgewählt', 'success');
                  onWorkflowTransition?.('quote_selected');
                } else {
                  console.error('❌ [QUOTE-MANAGEMENT] Failed to transition workflow state:', transitionResult.message);
                  showNotification('Lieferant fixiert, aber Workflow-Transition fehlgeschlagen', 'warning');
                }
              } catch (error) {
                console.error('❌ [QUOTE-MANAGEMENT] Error during workflow transition:', error);
                showNotification('Lieferant fixiert, aber Workflow-Transition fehlgeschlagen', 'warning');
              }
            }}
          >
            Fixieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Quote Selection Dialog */}
      <EmailQuoteSelectionDialog
        open={emailSelectionDialogOpen}
        onClose={() => setEmailSelectionDialogOpen(false)}
        onEmailSelected={handleEmailSelected}
        caseId={caseId}
        currentWorkflowState={currentWorkflowState}
        title="E-Mail für Angebots-Parsing auswählen"
      />
    </Box>
  );
};

export default QuoteManagementHub;
